// Privacy Policy for subswall.com operated by Dr. Gotthilf LLC (Wyoming).
// GDPR + CCPA aware. Not legal advice.

export default function Privacy(): JSX.Element {
  return (
    <article className="legal-page">
      <h1>Privacy Policy</h1>
      <p className="legal-meta">
        Effective date: 2026-05-30 · Last updated: 2026-05-30
      </p>

      <p>
        <em>
          This is the publicly-posted privacy policy for SubsWall. It is not
          legal advice for you. If you operate a business that processes
          personal data through the Platform, you should consult your own
          lawyer about your own obligations.
        </em>
      </p>

      <p>
        This Privacy Policy (the <strong>"Policy"</strong>) explains how{" "}
        <strong>Dr. Gotthilf LLC</strong>, a Wyoming limited liability company
        with its registered office at 30 N Gould St Ste R, Sheridan, WY 82801,
        United States (<strong>"Company"</strong>, <strong>"we"</strong>,{" "}
        <strong>"us"</strong>), collects, uses, shares, and protects personal
        information in connection with the SubsWall service available at{" "}
        <a href="https://subswall.com">https://subswall.com</a> (the{" "}
        <strong>"Platform"</strong>).
      </p>

      <h2>Table of contents</h2>
      <ul>
        <li><a href="#scope">1. Scope and roles</a></li>
        <li><a href="#collect">2. Information we collect</a></li>
        <li><a href="#use">3. How we use information</a></li>
        <li><a href="#basis">4. Lawful bases (EEA/UK)</a></li>
        <li><a href="#share">5. Subprocessors and sharing</a></li>
        <li><a href="#cookies">6. Cookies</a></li>
        <li><a href="#retention">7. Data retention</a></li>
        <li><a href="#rights">8. Your rights (GDPR)</a></li>
        <li><a href="#ccpa">9. Your rights (California / CCPA)</a></li>
        <li><a href="#transfers">10. International transfers</a></li>
        <li><a href="#security">11. Security</a></li>
        <li><a href="#children">12. Children</a></li>
        <li><a href="#breach">13. Breach notification</a></li>
        <li><a href="#changes">14. Changes to this Policy</a></li>
        <li><a href="#contact">15. Contact and DPO</a></li>
      </ul>

      <h2 id="scope">1. Scope and roles</h2>
      <p>
        This Policy applies to Creators, Donors, and visitors of the Platform.
        For most processing, the Company acts as an independent{" "}
        <strong>data controller</strong> with respect to the personal data it
        collects from you. For card-payment processing, Stripe, Inc. acts as
        an independent data controller under its own privacy policy.
      </p>

      <h2 id="collect">2. Information we collect</h2>
      <ul>
        <li>
          <strong>Account information (Creators).</strong> When you sign in
          via Twitch or Google OAuth we receive your platform user id, public
          display name, email address (where the OAuth provider releases it),
          locale, and avatar URL.
        </li>
        <li>
          <strong>Connected Account information (Creators).</strong> Stripe
          collects identity and payout information directly from you during
          Stripe Connect onboarding (legal name, address, date of birth, tax
          identifier, bank or card details). We receive a Stripe-issued
          identifier for your Connected Account and high-level status flags
          (e.g. "charges enabled", "payouts enabled"). We do not store your
          full bank account or tax ID on our servers.
        </li>
        <li>
          <strong>Donation information (Donors).</strong> When you send a
          Donation, Stripe collects your card details, billing address, and
          email directly through its Stripe-hosted payment form. We receive a
          Stripe-issued payment identifier, the amount, currency, status,
          country code, the Creator the Donation was directed to, and any
          optional message and display name you choose to attach.
        </li>
        <li>
          <strong>Card data.</strong> The Company never sees, stores, or
          processes your full primary account number (PAN), CVC, or expiry
          date. Card data flows directly from your browser to Stripe.
        </li>
        <li>
          <strong>Server logs and fraud signals.</strong> We log standard
          request metadata (IP address, user agent, request path, timestamp,
          referrer, response code) for security, abuse prevention,
          rate-limiting, and debugging.
        </li>
        <li>
          <strong>Communications.</strong> If you contact us by email we
          retain the email and our reply.
        </li>
      </ul>

      <h2 id="use">3. How we use information</h2>
      <ul>
        <li>Operate the Platform: authenticate Creators, route Donations,
          display Donation events on overlays, render Creator profiles.</li>
        <li>Process payments via Stripe and reconcile payouts.</li>
        <li>Prevent fraud, chargeback abuse, multi-accounting, and sanctions
          evasion.</li>
        <li>Send transactional emails (sign-in notices, payout failures,
          policy updates).</li>
        <li>Comply with law, respond to lawful requests from authorities,
          enforce these Terms, and protect the rights, property, and safety
          of the Company, our users, and the public.</li>
      </ul>
      <p>
        We do not sell personal information. We do not use personal
        information to train machine-learning models. We do not run
        advertising on the Platform in v1; if we ever do, we will update this
        Policy first.
      </p>

      <h2 id="basis">4. Lawful bases (EEA/UK)</h2>
      <p>
        If you are in the European Economic Area, the United Kingdom, or
        Switzerland, we rely on the following lawful bases under the GDPR /
        UK GDPR:
      </p>
      <ul>
        <li><strong>Contract</strong> — to provide the Platform you have
          asked us to provide (operating your Creator account, processing
          Donations you send).</li>
        <li><strong>Legitimate interests</strong> — to prevent fraud, secure
          the Platform, analyze service performance, and develop new
          features. We balance these interests against your rights and
          freedoms.</li>
        <li><strong>Legal obligation</strong> — to comply with tax,
          accounting, sanctions, and anti-money-laundering law.</li>
        <li><strong>Consent</strong> — where required (e.g. future marketing
          emails or non-essential cookies). You may withdraw consent at any
          time without affecting the lawfulness of prior processing.</li>
      </ul>

      <h2 id="share">5. Subprocessors and sharing</h2>
      <p>We share personal data with the following service providers:</p>
      <ul>
        <li>
          <strong>Stripe, Inc.</strong> — payment processing, Connect
          onboarding, payouts, fraud signals.{" "}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            stripe.com/privacy
          </a>
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> — application hosting (Pages and
          Workers), edge caching, DDoS protection, DNS, KV/D1 storage for
          application data.{" "}
          <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
            cloudflare.com/privacypolicy
          </a>
        </li>
        <li>
          <strong>Twitch Interactive, Inc.</strong> — OAuth sign-in for
          Creators.{" "}
          <a href="https://www.twitch.tv/p/legal/privacy-notice/" target="_blank" rel="noopener noreferrer">
            twitch.tv/p/legal/privacy-notice
          </a>
        </li>
        <li>
          <strong>Google LLC</strong> — OAuth sign-in for Creators (Google /
          YouTube).{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            policies.google.com/privacy
          </a>
        </li>
      </ul>
      <p>
        We may also disclose personal data to (a) law enforcement, regulators,
        or other authorities in response to a valid legal request, (b)
        professional advisors (lawyers, auditors, accountants) under
        confidentiality, and (c) a successor entity in connection with a
        merger, acquisition, financing, or sale of assets.
      </p>

      <h2 id="cookies">6. Cookies</h2>
      <p>
        We use only essential cookies in v1: a single HttpOnly, Secure,
        SameSite session cookie that keeps you signed in, and a short-lived
        CSRF token cookie. We do not use third-party advertising cookies or
        analytics cookies in v1. If we later add analytics or marketing
        cookies, we will deploy a consent banner that meets the GDPR /
        ePrivacy standard for users in the EEA, UK, and Switzerland and the
        CCPA "Do Not Sell or Share" standard for California residents before
        the cookies are set.
      </p>

      <h2 id="retention">7. Data retention</h2>
      <ul>
        <li><strong>Active accounts:</strong> we retain Creator account data
          for as long as the account is active.</li>
        <li><strong>Account deletion:</strong> after you delete your account
          we delete or anonymize the account profile within 30 days, except
          for data we are required to keep (see below).</li>
        <li><strong>Donation and financial records:</strong> retained for
          seven (7) years from the transaction date for tax, accounting, and
          anti-money-laundering compliance, consistent with U.S. and EU
          obligations.</li>
        <li><strong>Server logs:</strong> retained for up to 90 days for
          security and debugging, then deleted or aggregated.</li>
        <li><strong>Communications:</strong> retained for up to 24 months
          after the last interaction.</li>
      </ul>

      <h2 id="rights">8. Your rights (GDPR)</h2>
      <p>
        If you are in the EEA, UK, or Switzerland, you have the right to:
      </p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Rectify inaccurate or incomplete data.</li>
        <li>Erase your data ("right to be forgotten"), subject to legal
          retention duties listed in section 7.</li>
        <li>Restrict our processing of your data.</li>
        <li>Receive your data in a portable, machine-readable format.</li>
        <li>Object to processing based on legitimate interests, including
          profiling.</li>
        <li>Withdraw consent where processing is based on consent, at any
          time, without affecting the lawfulness of prior processing.</li>
        <li>Lodge a complaint with a supervisory authority — for example,
          the Spanish AEPD (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">aepd.es</a>),
          the Irish DPC, or your local DPA.</li>
      </ul>
      <p>
        To exercise these rights, email{" "}
        <a href="mailto:privacy@subswall.com">privacy@subswall.com</a>. We
        respond within 30 days (extendable by 60 days for complex requests,
        with notice).
      </p>

      <h2 id="ccpa">9. Your rights (California / CCPA)</h2>
      <p>
        If you are a California resident, you have the right to:
      </p>
      <ul>
        <li><strong>Know</strong> the categories and specific pieces of
          personal information we have collected about you in the past 12
          months.</li>
        <li><strong>Delete</strong> personal information we have collected,
          subject to statutory exceptions.</li>
        <li><strong>Correct</strong> inaccurate personal information.</li>
        <li><strong>Opt out of sale or sharing</strong> of personal
          information. <strong>We do not sell or share personal information
          in the CCPA sense</strong>, and we have no "Do Not Sell or Share"
          link because there is nothing to opt out of.</li>
        <li><strong>Non-discrimination</strong> — we will not deny service,
          charge a different price, or provide a different quality of service
          because you exercised a CCPA right.</li>
      </ul>
      <p>
        To exercise these rights, email{" "}
        <a href="mailto:privacy@subswall.com">privacy@subswall.com</a>. We
        will verify your identity by matching your request to information
        already in our records.
      </p>

      <h2 id="transfers">10. International transfers</h2>
      <p>
        The Platform is operated from the United States. Cloudflare runs an
        anycast edge network and may process your data in any country in
        which it operates a point of presence. Stripe processes payment data
        in the United States and other countries. If you are in the EEA, UK,
        or Switzerland, personal data is transferred to the United States and
        other third countries on the basis of the European Commission's
        Standard Contractual Clauses (SCCs), the UK International Data
        Transfer Addendum, and/or other valid transfer mechanisms.
      </p>

      <h2 id="security">11. Security</h2>
      <p>
        We use industry-standard safeguards: TLS for data in transit,
        encryption at rest for application databases, signed JWT session
        tokens with short rotation windows, IP-restricted API tokens for
        infrastructure, least-privilege access for staff, and segregation of
        production and development environments. No system is perfectly
        secure; please use a strong, unique password on the OAuth account
        you sign in with.
      </p>

      <h2 id="children">12. Children</h2>
      <p>
        The Platform is not directed to children. We do not knowingly collect
        personal data from anyone under 13 years of age (or under 16 in the
        EEA / UK). If you believe a child has provided us with personal data,
        email <a href="mailto:privacy@subswall.com">privacy@subswall.com</a>{" "}
        and we will delete it.
      </p>

      <h2 id="breach">13. Breach notification</h2>
      <p>
        If we become aware of a personal data breach that is likely to result
        in a risk to your rights and freedoms, we will notify the competent
        supervisory authority without undue delay and, where feasible, within
        72 hours of becoming aware, as required by Article 33 GDPR. Where the
        breach is likely to result in a high risk to your rights and
        freedoms, we will also notify you without undue delay.
      </p>

      <h2 id="changes">14. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. If we make a material
        change, we will provide notice by email (to the address on your
        account) and by posting the updated Policy on the Platform with a
        new effective date.
      </p>

      <h2 id="contact">15. Contact and DPO</h2>
      <p>
        For any privacy question or to exercise a right under this Policy:
      </p>
      <p>
        Dr. Gotthilf LLC<br />
        Attn: Privacy / Data Protection<br />
        30 N Gould St Ste R<br />
        Sheridan, WY 82801<br />
        United States<br />
        Email: <a href="mailto:privacy@subswall.com">privacy@subswall.com</a>{" "}
        (general support:{" "}
        <a href="mailto:support@subswall.com">support@subswall.com</a>)
      </p>
      <p>
        We have not formally designated a Data Protection Officer because we
        do not meet the GDPR Article 37 threshold. Privacy inquiries reach
        the same internal team via privacy@subswall.com.
      </p>
    </article>
  );
}
